import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { Button, Input, Snackbar } from '@paysense-india-services/elevate-web'

import { ROUTES } from '@/constants/routes'

import { useProductConfig } from '@/contexts/ProductConfigContext'

import useFetch from '@/hooks/useFetch'

import { decodeEmailFromQuery } from '@/utils/urlParams'

import AuthFormShell from '@/components/layouts/AuthFormShell'

import { PRODUCT_OPERATIONS } from '@/config/productApi'
import { getStoredMobile, setStoredOtpId, setStoredPendingEmailOtp } from '@/config/sourceStorage'

import {
  DEFAULT_CAN_RESEND_AFTER,
  DEFAULT_EXPIRES_IN,
  EMAIL_REGEX,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES
} from './constants'

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams()
  const emailFromUrl = decodeEmailFromQuery(searchParams.get('email'))
  const [email, setEmail] = useState(emailFromUrl)
  const navigate = useNavigate()
  const { appendProductParams } = useProductConfig()
  const { loading, apiRequest } = useFetch()

  useEffect(() => {
    if (emailFromUrl) {
      setEmail(emailFromUrl)
    }
  }, [emailFromUrl])

  const validateEmail = () => {
    if (!email) {
      Snackbar.show(ERROR_MESSAGES.EMPTY_EMAIL, { type: 'error' })
      return false
    }

    if (!EMAIL_REGEX.test(email)) {
      Snackbar.show(ERROR_MESSAGES.INVALID_EMAIL, { type: 'error' })
      return false
    }

    return true
  }

  const handleSuccess = result => {
    if (!result?.ok) {
      Snackbar.show(ERROR_MESSAGES.SEND_FAILED, { type: 'error' })
      return
    }

    Snackbar.show(result.message ?? SUCCESS_MESSAGES.OTP_SENT, { type: 'success' })

    if (result.otpId) setStoredOtpId(result.otpId)
    setStoredPendingEmailOtp(email)

    const queryParams = appendProductParams({
      email,
      expires_in: String(result.expiresInSeconds ?? DEFAULT_EXPIRES_IN),
      can_resend_after: String(result.canResendAfterSeconds ?? DEFAULT_CAN_RESEND_AFTER)
    })
    navigate(`${ROUTES.OTP_VERIFICATION}?${queryParams.toString()}`)
  }

  const handleError = err => {
    setStoredPendingEmailOtp(null)
    setStoredOtpId(null)
    const errorMessage = err?.parsedMessage || ERROR_MESSAGES.SEND_FAILED
    Snackbar.show(errorMessage, { type: 'error' })
  }

  const handleSendOTP = () => {
    if (!validateEmail()) return

    apiRequest(
      PRODUCT_OPERATIONS.GENERATE_EMAIL_OTP,
      { email, mobile: getStoredMobile() },
      {
        onSuccess: handleSuccess,
        onError: handleError
      }
    )
  }

  return (
    <AuthFormShell headerVariant="logo">
      <h1 className="ew-typography-large ew-fw-bold ew-color-primary">Verify your email</h1>
      <p className="ew-typography-normal ew-color-secondary ew-fw-regular ew-mb-32">
        We will send an OTP to your registered email to verify your email
      </p>

      <div className="ew-w-100">
        <Input
          disabled={loading}
          hintText="Enter your email ID"
          label="Email ID"
          onChange={val => setEmail(val)}
          type="email"
          value={email}
        />
        <Button
          className="ew-mt-24"
          fullWidth
          isDisabled={loading || !email}
          isLoading={loading}
          onClick={handleSendOTP}
          size="medium"
          type="default"
          variant="brand"
        >
          Send OTP
        </Button>
      </div>
      <Snackbar />
    </AuthFormShell>
  )
}

export default EmailVerificationPage
